<script lang="ts" setup>
const { t } = useI18n();
const body: Ref<FormData> = ref(new FormData());

const route = useRoute();
const uid = ref<string>(route.query.u as string);

const { data, execute, status } = useFetch("/api/get-hash", {
  method: "POST",
  immediate: false,
  watch: false,
  body,
});

const isLoading = computed(() => status.value === "pending");

watch(uid, (newUid) => {
  body.value.set("uid", newUid);
});

function setFile(files: FileList) {
  body.value = new FormData();
  if (files.length && files[0]) {
    body.value.append("file", files[0]);
    body.value.set("uid", uid.value);
  }
}
</script>

<template>
  <div class="mx-auto my-24 flex max-w-7xl flex-col items-center px-6 lg:my-32 lg:px-8">
    <div class="mx-auto mt-24 max-w-2xl text-center lg:mx-0">
      <h2 class="font-serif text-4xl font-semibold text-gray-900 sm:text-5xl">
        {{ $t('verify.title') }}
      </h2>
      <p class="mt-2 text-lg/8 text-gray-600">
        {{ $t('verify.subtitle') }}
      </p>
    </div>

    <div class="mx-auto my-20 flex w-full max-w-xl flex-col lg:mx-0">
      <form @submit.prevent="() => execute()">
        <UFormGroup
          :label="$t('verify.documentId')"
          :hint="$t('verify.optional')"
          class="mb-4"
        >
          <UInput
            v-model="uid"
            type="text"
            name="uid"
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </UFormGroup>

        <UFormGroup
          :label="$t('verify.documentToVerify')"
          class="mb-4"
        >
          <UInput
            type="file"
            name="file"
            icon="i-heroicons-folder"
            @change="setFile"
          />
        </UFormGroup>

        <UButton
          type="submit"
          size="lg"
          class="w-full justify-center"
          :loading="isLoading"
        >
          {{ $t('verify.verifyDocument') }}
        </UButton>
      </form>

      <div
        v-if="data?.status === 'valid'"
        class="mt-6 flex flex-col items-center justify-center gap-6 rounded-lg border border-emerald-300 bg-emerald-50 p-6 shadow-sm"
      >
        <Icon
          name="lucide:circle-check-big"
          class="size-12 text-emerald-500"
        />
        <p class="text-xl font-semibold text-emerald-700">
          {{ $t('verify.valid') }}
        </p>
        <p class="text-center text-sm text-gray-500">
          {{ $t('verify.hashesMatch') }}
        </p>

        <!-- Hash -->
        <div class="w-full rounded-lg bg-white p-4">
          <pre class="overflow-x-scroll font-mono text-sm text-gray-800 scrollbar-hide">{{ data?.hash }}</pre>
        </div>
      </div>
      <div
        v-else-if="data?.status === 'unknown'"
        class="mt-6 flex flex-col items-center justify-center gap-6 rounded-lg border border-yellow-300 bg-yellow-50 p-6 shadow-sm"
      >
        <Icon
          name="lucide:shield-question"
          class="size-12 text-yellow-500"
        />
        <p class="text-xl font-semibold text-yellow-700">
          {{ $t('verify.manualVerification') }}
        </p>
        <p class="text-center text-sm text-gray-500">
          {{ $t('verify.manualVerificationDesc') }}
        </p>

        <!-- Hash -->
        <div class="w-full rounded-lg bg-white p-4">
          <pre class="overflow-x-scroll font-mono text-sm text-gray-800 scrollbar-hide">{{ data?.hash }}</pre>
        </div>
      </div>
      <div
        v-else-if="data"
        class="mt-6 flex flex-col items-center justify-center gap-6 rounded-lg border border-red-300 bg-red-50 p-6 shadow-sm"
      >
        <Icon
          name="lucide:circle-x"
          class="size-12 text-red-500"
        />
        <p class="text-xl font-semibold text-red-700">
          {{ $t('verify.invalid') }}
        </p>
        <p class="text-center text-sm text-gray-500">
          {{ $t('verify.hashesDoNotMatch') }}
        </p>

        <!-- Hash -->
        <div class="w-full rounded-lg bg-white p-4">
          <pre class="overflow-x-scroll font-mono text-sm text-gray-800 scrollbar-hide">{{ data?.hash }}</pre>
        </div>
      </div>
    </div>

    <LandingEndCta />
  </div>
</template>
